// src/components/ProposalTypeChoice.tsx
// Clean card-based proposal type selection with modern SaaS aesthetics

import React from 'react';
import {
  X,
  Target,
  CheckCircle,
  Link2,
  Database,
  Shield,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { Contract } from '../types';

interface ProposalTypeChoiceProps {
  contract: Contract;
  onClose: () => void;
  onSelectType: (type: 'basic' | 'enhanced' | 'source-grounded') => void;
}

interface ProposalOptionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  badge?: { label: string; variant: 'new' | 'premium' };
  features: Array<{ icon: React.ReactNode; text: string }>;
  tagline: string;
  onClick: () => void;
  variant: 'default' | 'featured' | 'premium';
}

function ProposalOption({
  title,
  description,
  icon,
  badge,
  features,
  tagline,
  onClick,
  variant,
}: ProposalOptionProps) {
  const baseStyles =
    'relative flex flex-col rounded-xl border transition-all duration-200 cursor-pointer group';

  const variantStyles = {
    default:
      'border-bw-border bg-bw-surface hover:border-bw-border hover:shadow-card',
    featured:
      'border-bw-border bg-bw-surface hover:border-bw-border hover:shadow-card ring-1 ring-gray-100',
    premium:
      'border-slate-200 bg-bw-surface hover:border-slate-300 hover:shadow-lg',
  };

  const badgeStyles = {
    new: 'bg-success-bg0 text-white',
    premium: 'bg-slate-700 text-white',
  };

  return (
    <div className={`${baseStyles} ${variantStyles[variant]}`} onClick={onClick}>
      {/* Badge */}
      {badge && (
        <div className="absolute -top-2.5 left-4">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${badgeStyles[badge.variant]}`}
          >
            {badge.variant === 'premium' && <Sparkles className="h-3 w-3 mr-1" />}
            {badge.label}
          </span>
        </div>
      )}

      <div className="p-6 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 p-2.5 rounded-lg bg-slate-100 text-slate-600 group-hover:bg-slate-200 transition-colors">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-bw-ink">{title}</h3>
            <p className="text-sm text-bw-muted mt-0.5 leading-relaxed">{description}</p>
          </div>
        </div>

        {/* Features */}
        <div className="flex-1 space-y-2.5 mt-2">
          {features.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2.5 text-sm text-bw-muted">
              <span className="flex-shrink-0 text-slate-400">{feature.icon}</span>
              <span>{feature.text}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-5 pt-4 border-t border-bw-border">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-bw-muted uppercase tracking-wide">
              {tagline}
            </span>
            <ArrowRight className="h-4 w-4 text-bw-muted group-hover:text-bw-muted group-hover:translate-x-0.5 transition-all" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProposalTypeChoice({ contract, onClose, onSelectType }: ProposalTypeChoiceProps) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-bw-surface rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-bw-border bg-bw-surface-subtle">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-bw-ink">Choose Proposal Type</h2>
              <p className="text-sm text-bw-muted mt-1">
                Select the approach that best fits your needs for this opportunity
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 -m-2 text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Options Grid — Source-Grounded is currently the supported generator.
            (Basic/Enhanced cards were retired; the modal is kept so simpler paths
            can be re-added here later.) */}
        <div className="p-6">
          <div className="grid grid-cols-1 max-w-md mx-auto gap-5">
            {/* Source-Grounded Option */}
            <ProposalOption
              variant="premium"
              title="Source-Grounded"
              description="Every claim traces to documented evidence"
              icon={<Link2 className="h-5 w-5" />}
              badge={{ label: 'Traceable', variant: 'premium' }}
              features={[
                { icon: <Database className="h-4 w-4" />, text: 'Evidence library from profile' },
                { icon: <Link2 className="h-4 w-4" />, text: 'Inline citations [PP-003]' },
                { icon: <Target className="h-4 w-4" />, text: 'Requirement matching' },
                { icon: <Shield className="h-4 w-4" />, text: '100% traceability score' },
              ]}
              tagline="Full audit trail"
              onClick={() => onSelectType('source-grounded')}
            />
          </div>

          {/* Contract Context */}
          <div className="mt-6 p-4 bg-bw-surface-subtle rounded-xl border border-bw-border">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-1.5 w-1.5 rounded-full bg-success-bg0" />
              <span className="text-xs font-medium text-bw-muted uppercase tracking-wide">
                Selected Contract
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-bw-muted">Title</span>
                <p className="font-medium text-bw-ink truncate mt-0.5">{contract.title}</p>
              </div>
              <div>
                <span className="text-bw-muted">Agency</span>
                <p className="font-medium text-bw-ink mt-0.5">{contract.agency}</p>
              </div>
              <div>
                <span className="text-bw-muted">Value</span>
                <p className="font-medium text-bw-ink mt-0.5">
                  {contract.fundingAmount
                    ? `$${contract.fundingAmount.toLocaleString()}`
                    : 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Info Note */}
          <div className="mt-4 flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg text-sm text-slate-600">
            <CheckCircle className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <span>All proposals are saved automatically and can be edited or exported later</span>
          </div>
        </div>
      </div>
    </div>
  );
}
